import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import styles from './Dropdown.module.scss';

const MENU_GAP = 4;

const Dropdown = ({
  trigger,
  children,
  align = 'left',
  disabled = false,
  ...theRestOfTheProps
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      const clickedTrigger = triggerRef.current?.contains(event.target);
      const clickedMenu = menuRef.current?.contains(event.target);
      if (!clickedTrigger && !clickedMenu) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    const handleDismiss = () => setIsOpen(false);

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    window.addEventListener('scroll', handleDismiss, true);
    window.addEventListener('resize', handleDismiss);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('scroll', handleDismiss, true);
      window.removeEventListener('resize', handleDismiss);
    };
  }, [isOpen]);

  useLayoutEffect(() => {
    if (!isOpen || !menuRef.current || !triggerRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const menuRect = menuRef.current.getBoundingClientRect();
    const overflowsBottom = triggerRect.bottom + MENU_GAP + menuRect.height > window.innerHeight;

    const style = {};

    if (overflowsBottom) {
      style.top = triggerRect.top - MENU_GAP - menuRect.height;
    } else {
      style.top = triggerRect.bottom + MENU_GAP;
    }

    if (align === 'right') {
      style.right = window.innerWidth - triggerRect.right;
    } else {
      style.left = triggerRect.left;
    }

    setMenuStyle(style);
  }, [isOpen, align]);

  const openMenu = () => {
    if (!triggerRef.current) return;
    setIsOpen(true);
  };

  const handleToggle = () => {
    if (disabled) return;
    if (isOpen) setIsOpen(false);
    else openMenu();
  };

  const handleItemClick = (onClick) => {
    return (event) => {
      if (onClick) onClick(event);
      setIsOpen(false);
    };
  };

  const className = clsx(
    styles.Dropdown,
    { [styles['Dropdown--disabled']]: disabled }
  );

  const menu = isOpen
    ? createPortal(
        <div
          className={styles.Dropdown__menu}
          role="menu"
          ref={menuRef}
          style={menuStyle}
        >
          {React.Children.map(children, (child) => {
            if (React.isValidElement(child) && child.type === DropdownItem) {
              return React.cloneElement(child, {
                onClick: handleItemClick(child.props.onClick),
              });
            }
            return child;
          })}
        </div>,
        document.body
      )
    : null;

  return (
    <div className={className} ref={triggerRef} {...theRestOfTheProps}>
      <div
        className={styles.Dropdown__trigger}
        onClick={handleToggle}
        role="button"
        aria-haspopup="true"
        aria-expanded={isOpen}
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleToggle();
          }
        }}
      >
        {trigger}
      </div>
      {menu}
    </div>
  );
};

const DropdownItem = ({
  children,
  onClick,
  disabled = false,
  destructive = false,
  ...theRestOfTheProps
}) => {
  const className = clsx(
    styles.DropdownItem,
    {
      [styles['DropdownItem--disabled']]: disabled,
      [styles['DropdownItem--destructive']]: destructive,
    }
  );

  const handleClick = (event) => {
    if (!disabled && onClick) {
      onClick(event);
    }
  };

  return (
    <button
      type="button"
      className={className}
      onClick={handleClick}
      disabled={disabled}
      role="menuitem"
      {...theRestOfTheProps}
    >
      {children}
    </button>
  );
};

const DropdownDivider = () => {
  return <div className={styles.DropdownDivider} role="separator" />;
};

Dropdown.Item = DropdownItem;
Dropdown.Divider = DropdownDivider;

export default Dropdown;
